import React, { useMemo } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { addColorOpacity } from '../../shared/utils/styles';
import { FlagType, FLAG_CONFIG, DataFieldDefinition } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';

export interface FlagSuggestion {
  flag: FlagType;
  condition: string;
  reason?: string; // Auto-generated reason based on data
  confidence: 'high' | 'medium' | 'low';
}

export interface FlagSuggestionsProps {
  suggestedFlags?: { flag: FlagType; condition: string }[];
  dataFields?: DataFieldDefinition[];
  dataValues: Record<string, any>;
  currentFlag: FlagType | null | undefined;
  onAcceptSuggestion: (flag: FlagType) => void;
  itemCode?: string;
}

/**
 * Analyzes data values and generates smart flag suggestions
 */
function analyzeSuggestions(
  suggestedFlags: { flag: FlagType; condition: string }[] | undefined,
  dataFields: DataFieldDefinition[] | undefined,
  dataValues: Record<string, any>,
  itemCode?: string
): FlagSuggestion[] {
  const suggestions: FlagSuggestion[] = [];
  
  if (!suggestedFlags || suggestedFlags.length === 0) {
    return suggestions;
  }

  // Smart analysis based on common patterns
  for (const sf of suggestedFlags) {
    let matched = false;
    let reason = '';
    let confidence: 'high' | 'medium' | 'low' = 'low';

    // Acreage verification - check for discrepancy
    if (itemCode === 'acreage_verify') {
      const listed = dataValues.listed_acres;
      const verified = dataValues.verified_acres;
      if (listed && verified && listed > 0) {
        const discrepancy = Math.abs((verified - listed) / listed) * 100;
        if (discrepancy > 5 && sf.flag === 'red') {
          matched = true;
          reason = `${discrepancy.toFixed(1)}% discrepancy between listed (${listed}) and verified (${verified}) acres`;
          confidence = 'high';
        } else if (discrepancy >= 2 && discrepancy <= 5 && sf.flag === 'yellow') {
          matched = true;
          reason = `${discrepancy.toFixed(1)}% discrepancy - minor but worth noting`;
          confidence = 'high';
        }
      }
    }

    // Liens check
    if (itemCode === 'liens_check') {
      if (dataValues.liens_found === true && (sf.flag === 'red' || sf.flag === 'big_red')) {
        matched = true;
        reason = 'Outstanding liens found on property';
        confidence = 'high';
      } else if (dataValues.liens_found === false && sf.flag === 'green') {
        matched = true;
        reason = 'No liens found';
        confidence = 'high';
      }
    }

    // Back taxes check
    if (itemCode === 'back_taxes') {
      if (dataValues.taxes_current === false && sf.flag === 'red') {
        matched = true;
        reason = dataValues.amount_owed 
          ? `Delinquent taxes: $${dataValues.amount_owed.toLocaleString()}`
          : 'Delinquent taxes on property';
        confidence = 'high';
      } else if (dataValues.taxes_current === true && sf.flag === 'green') {
        matched = true;
        reason = 'Property taxes are current';
        confidence = 'high';
      }
    }

    // Title search
    if (itemCode === 'title_search') {
      const issues = dataValues.issues_found;
      if (Array.isArray(issues) && issues.length > 0 && (sf.flag === 'red' || sf.flag === 'big_red')) {
        matched = true;
        reason = `${issues.length} title issue(s) found`;
        confidence = 'high';
      } else if ((!issues || issues.length === 0) && sf.flag === 'green') {
        matched = true;
        reason = 'Clean title, no issues found';
        confidence = 'high';
      }
    }

    // Boundary disputes
    if (itemCode === 'boundary_disputes') {
      if (dataValues.disputes_exist === true && (sf.flag === 'red' || sf.flag === 'big_red')) {
        matched = true;
        reason = 'Active boundary dispute exists';
        confidence = 'high';
      } else if (dataValues.disputes_exist === false && sf.flag === 'green') {
        matched = true;
        reason = 'No boundary disputes';
        confidence = 'high';
      }
    }

    // Encroachments
    if (itemCode === 'encroachments') {
      if (dataValues.encroachments_found === true && (sf.flag === 'red' || sf.flag === 'big_red')) {
        matched = true;
        reason = `Encroachment found: ${dataValues.encroachment_type || 'details unknown'}`;
        confidence = 'high';
      } else if (dataValues.encroachments_found === false && sf.flag === 'green') {
        matched = true;
        reason = 'No encroachments found';
        confidence = 'high';
      }
    }

    // Zoning permitted uses
    if (itemCode === 'zoning_permitted') {
      if (dataValues.intended_use_permitted === false && sf.flag === 'big_red') {
        matched = true;
        reason = 'Intended use is NOT permitted by current zoning';
        confidence = 'high';
      } else if (dataValues.intended_use_permitted === true && sf.flag === 'big_green') {
        matched = true;
        reason = 'Intended use is permitted by-right';
        confidence = 'high';
      }
    }

    // Ag tax rollback
    if (itemCode === 'ag_tax_status') {
      if (dataValues.rollback_risk === true && sf.flag === 'red') {
        matched = true;
        reason = dataValues.estimated_rollback 
          ? `Ag rollback risk: ~$${dataValues.estimated_rollback.toLocaleString()}`
          : 'Ag rollback taxes may be due on change of use';
        confidence = 'high';
      } else if (dataValues.has_ag_exemption === true && dataValues.rollback_risk !== true && sf.flag === 'big_green') {
        matched = true;
        reason = 'Ag exemption in place, low rollback risk';
        confidence = 'high';
      }
    }

    // Tax assessment vs asking (good deal detection)
    if (itemCode === 'tax_vs_asking') {
      const asking = dataValues.asking_price;
      const assessed = dataValues.total_assessed || dataValues.land_value;
      if (asking && assessed) {
        const ratio = (assessed / asking) * 100;
        if (ratio > 80 && sf.flag === 'big_green') {
          matched = true;
          reason = `Assessment is ${ratio.toFixed(0)}% of asking - potential good deal`;
          confidence = 'medium';
        } else if (ratio < 50 && sf.flag === 'yellow') {
          matched = true;
          reason = `Assessment is only ${ratio.toFixed(0)}% of asking - tax increase likely after purchase`;
          confidence = 'high';
        }
      }
    }

    // Generic boolean field checks
    if (!matched && dataFields) {
      for (const field of dataFields) {
        if (field.type === 'boolean') {
          const val = dataValues[field.key];
          // Check if condition mentions this field
          const conditionLower = sf.condition.toLowerCase();
          const labelLower = field.label.toLowerCase();
          
          if (conditionLower.includes(labelLower) || conditionLower.includes(field.key)) {
            if (val === true || val === false) {
              // Provide suggestion but with low confidence since we're guessing
              suggestions.push({
                flag: sf.flag,
                condition: sf.condition,
                confidence: 'low',
              });
            }
          }
        }
      }
    }

    // Add matched suggestion
    if (matched) {
      suggestions.push({
        flag: sf.flag,
        condition: sf.condition,
        reason,
        confidence,
      });
    }
  }

  // Also show unmatched suggestions as hints (low confidence)
  for (const sf of suggestedFlags) {
    const alreadyAdded = suggestions.some(s => s.flag === sf.flag && s.condition === sf.condition);
    if (!alreadyAdded) {
      // Check if any data has been entered
      const hasData = Object.values(dataValues).some(v => v !== null && v !== undefined && v !== '');
      if (hasData) {
        suggestions.push({
          flag: sf.flag,
          condition: sf.condition,
          confidence: 'low',
        });
      }
    }
  }

  return suggestions;
}

export function FlagSuggestions({
  suggestedFlags,
  dataFields,
  dataValues,
  currentFlag,
  onAcceptSuggestion,
  itemCode,
}: FlagSuggestionsProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const suggestionStyles = makeSuggestionStyles(tokens, utils);

  const suggestions = useMemo(
    () => analyzeSuggestions(suggestedFlags, dataFields, dataValues, itemCode),
    [suggestedFlags, dataFields, dataValues, itemCode]
  );

  // Filter out suggestions that match the current flag
  const relevantSuggestions = suggestions.filter(s => s.flag !== currentFlag);

  // Sort by confidence (high first)
  const sortedSuggestions = relevantSuggestions.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.confidence] - order[b.confidence];
  });

  // Only show high/medium confidence, or show low if no flag is set
  const displaySuggestions = sortedSuggestions.filter(
    s => s.confidence !== 'low' || !currentFlag
  );

  if (displaySuggestions.length === 0) {
    return null;
  }

  return (
    <View style={suggestionStyles.container}>
      <View style={suggestionStyles.header}>
        <Ionicons name="bulb-outline" size={14} color={tokens.color.warning || '#EAB308'} />
        <Text style={suggestionStyles.headerText}>Flag Suggestions</Text>
      </View>
      <View style={suggestionStyles.suggestionsList}>
        {displaySuggestions.slice(0, 3).map((suggestion, index) => {
          const flagConfig = FLAG_CONFIG[suggestion.flag];
          
          return (
            <View key={`${suggestion.flag}-${index}`} style={suggestionStyles.suggestionItem}>
              <View style={suggestionStyles.suggestionContent}>
                <View style={[suggestionStyles.flagIndicator, { backgroundColor: flagConfig.color }]}>
                  <Ionicons name={flagConfig.icon as any} size={12} color="#fff" />
                </View>
                <View style={suggestionStyles.suggestionTextContainer}>
                  <Text style={suggestionStyles.suggestionLabel}>
                    {flagConfig.label}
                    {suggestion.confidence === 'high' && (
                      <Text style={suggestionStyles.confidenceHigh}> • Recommended</Text>
                    )}
                  </Text>
                  {suggestion.reason ? (
                    <Text style={suggestionStyles.suggestionReason} numberOfLines={2}>
                      {suggestion.reason}
                    </Text>
                  ) : (
                    <Text style={suggestionStyles.suggestionCondition} numberOfLines={2}>
                      {suggestion.condition}
                    </Text>
                  )}
                </View>
              </View>
              <Pressable
                onPress={() => onAcceptSuggestion(suggestion.flag)}
                style={({ pressed }) => [
                  suggestionStyles.acceptButton,
                  pressed && suggestionStyles.acceptButtonPressed,
                ]}
              >
                <Text style={suggestionStyles.acceptButtonText}>Set Flag</Text>
              </Pressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function makeSuggestionStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils']
) {
  return StyleSheet.create({
    container: {
      marginTop: utils.spacing.md,
      padding: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.warning || '#EAB308', 0.08),
      borderWidth: 1,
      borderColor: addColorOpacity(tokens.color.warning || '#EAB308', 0.2),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      marginBottom: utils.spacing.sm,
    },
    headerText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.warning || '#EAB308',
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    suggestionsList: {
      gap: utils.spacing.sm,
    },
    suggestionItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: utils.spacing.sm,
      padding: utils.spacing.sm,
      borderRadius: utils.radius.sm,
      backgroundColor: tokens.color.surface,
    },
    suggestionContent: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: utils.spacing.sm,
      flex: 1,
      minWidth: 0,
    },
    flagIndicator: {
      width: 24,
      height: 24,
      borderRadius: utils.radius.sm,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    suggestionTextContainer: {
      flex: 1,
      minWidth: 0,
    },
    suggestionLabel: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    confidenceHigh: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.success,
    },
    suggestionReason: {
      fontSize: utils.typography.sizes.xs,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    suggestionCondition: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      fontStyle: 'italic',
      marginTop: 2,
    },
    acceptButton: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    acceptButtonPressed: {
      opacity: 0.7,
    },
    acceptButtonText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

